import { api } from "./api/client";
import type { TransactionInput } from "./types";
import { isValidDateKey } from "./utils/date";

type ToolDefinition = {
  name: string;
  title: string;
  description: string;
  inputSchema: object;
  annotations: { readOnlyHint: boolean; untrustedContentHint: boolean };
  execute: (input: unknown) => Promise<unknown>;
};

declare global {
  interface Document {
    modelContext?: {
      registerTool: (tool: ToolDefinition, options?: { signal?: AbortSignal }) => void | Promise<void>;
    };
  }
}

const categories = ["餐饮", "交通", "购物", "娱乐", "居住", "医疗", "工资", "投资", "其他"];

function assertTransactionInput(input: unknown): TransactionInput {
  if (!input || typeof input !== "object") throw new Error("账目信息不能为空。");
  const value = input as Record<string, unknown>;
  if (value.type !== "expense" && value.type !== "income") throw new Error("type 必须是 expense 或 income。");
  if (!Number.isSafeInteger(value.amount_cents) || Number(value.amount_cents) <= 0) throw new Error("amount_cents 必须是正整数。");
  if (typeof value.category !== "string" || !categories.includes(value.category)) throw new Error("分类无效。");
  if (typeof value.note !== "string" || value.note.length > 120) throw new Error("备注无效。");
  if (typeof value.transaction_date !== "string" || !isValidDateKey(value.transaction_date)) throw new Error("日期无效。");
  if (typeof value.transaction_time !== "string" || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value.transaction_time)) throw new Error("时间无效。");
  return value as unknown as TransactionInput;
}

export function registerLedgerTools(): () => void {
  const context = document.modelContext;
  if (!context?.registerTool) return () => undefined;
  const lifecycle = new AbortController();

  const register = (tool: ToolDefinition) => {
    try {
      void Promise.resolve(context.registerTool(tool, { signal: lifecycle.signal })).catch(() => undefined);
    } catch {
      // WebMCP is optional and must not affect the visible app.
    }
  };

  register({
    name: "create_transaction",
    title: "新增账目",
    description: "在个人账本中新增一笔收入或支出，金额使用整数分。",
    inputSchema: {
      type: "object",
      properties: {
        type: { type: "string", enum: ["expense", "income"] },
        amount_cents: { type: "integer", minimum: 1 },
        category: { type: "string", enum: categories },
        note: { type: "string", maxLength: 120 },
        transaction_date: { type: "string", pattern: "^\\d{4}-\\d{2}-\\d{2}$" },
        transaction_time: { type: "string", pattern: "^([01]\\d|2[0-3]):[0-5]\\d$" },
      },
      required: ["type", "amount_cents", "category", "note", "transaction_date", "transaction_time"],
      additionalProperties: false,
    },
    annotations: { readOnlyHint: false, untrustedContentHint: false },
    async execute(input) {
      const result = await api.createTransaction(assertTransactionInput(input));
      window.dispatchEvent(new Event("ledger-data-changed"));
      return { id: result.transaction.id, status: "created" };
    },
  });

  register({
    name: "update_daily_expense_limit",
    title: "修改每日支出界限",
    description: "更新账本中用于标记超支日期的每日金额界限，金额使用整数分。",
    inputSchema: {
      type: "object",
      properties: { daily_expense_limit_cents: { type: "integer", minimum: 1 } },
      required: ["daily_expense_limit_cents"],
      additionalProperties: false,
    },
    annotations: { readOnlyHint: false, untrustedContentHint: false },
    async execute(input) {
      const value = (input as { daily_expense_limit_cents?: unknown })?.daily_expense_limit_cents;
      if (!Number.isSafeInteger(value) || Number(value) <= 0) throw new Error("每日支出界限必须是正整数分。");
      const result = await api.updateSettings(Number(value));
      window.dispatchEvent(new Event("ledger-data-changed"));
      return { daily_expense_limit_cents: result.daily_expense_limit_cents, status: "updated" };
    },
  });

  return () => lifecycle.abort();
}
